import React, { useState, useEffect } from 'react';
import { X, Calendar, MapPin, Ticket, Copy, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// KazBuild 2026 exhibition ends on Sept 4, 2026. Hide after this date.
const EXHIBITION_END = new Date('2026-09-04T23:59:59+06:00');
const DISMISS_KEY = 'gv_kazbuild_2026_dismissed_at';
const DISMISS_TTL_DAYS = 7;
const REGISTER_URL = 'https://kazbuild.kz/ru/poluchite-bilet-na-vystavku';
const PROMO_CODE = 'KBAH2HA65W';

const content = {
  ru: {
    heading: 'Мы участвуем в KazBuild 2026',
    subheading: 'Официальный завод GIPSOVINIL.KZ приглашает вас на международную строительную выставку',
    dates: '2–4 сентября 2026',
    place: 'г. Алматы, ул. Тимирязева 42к10, Атакент ЭКСПО',
    booth: 'Наш стенд: 10-706',
    promoLabel: 'Промокод на бесплатный билет',
    copied: 'Скопировано',
    copy: 'Копировать',
    register: 'Получить билет',
    later: 'Напомнить позже',
    footer: 'На выставке будет присутствовать собственник компании — обсудим ваш проект лично.'
  },
  kz: {
    heading: 'Біз KazBuild 2026 көрмесіне қатысамыз',
    subheading: 'GIPSOVINIL.KZ ресми зауыты сізді халықаралық құрылыс көрмесіне шақырады',
    dates: '2–4 қыркүйек 2026',
    place: 'Алматы қ., Тимирязев к. 42к10, Атакент ЭКСПО',
    booth: 'Стенд: 10-706',
    promoLabel: 'Тегін билетке промокод',
    copied: 'Көшірілді',
    copy: 'Көшіру',
    register: 'Билет алу',
    later: 'Кейін еске салу',
    footer: 'Көрмеде компания иесі болады — жобаңызды жеке талқылаймыз.'
  },
  en: {
    heading: 'We are exhibiting at KazBuild 2026',
    subheading: 'Official GIPSOVINIL.KZ plant invites you to the international construction expo',
    dates: 'September 2–4, 2026',
    place: 'Almaty, Timiryazev St. 42к10, Atakent EXPO',
    booth: 'Booth: 10-706',
    promoLabel: 'Promo code for a free ticket',
    copied: 'Copied',
    copy: 'Copy',
    register: 'Get ticket',
    later: 'Remind me later',
    footer: 'The company owner will be at the booth — let\'s discuss your project in person.'
  },
  ar: {
    heading: 'نشارك في معرض KazBuild 2026',
    subheading: 'مصنع GIPSOVINIL.KZ الرسمي يدعوكم لزيارة المعرض الدولي للبناء',
    dates: '2–4 سبتمبر 2026',
    place: 'ألماتي، شارع تيميريازيف 42/10، أتاكنت إكسبو',
    booth: 'جناحنا: 10-706',
    promoLabel: 'رمز ترويجي للحصول على تذكرة مجانية',
    copied: 'تم النسخ',
    copy: 'نسخ',
    register: 'احصل على التذكرة',
    later: 'ذكّرني لاحقاً',
    footer: 'سيكون مالك الشركة حاضراً في المعرض — لنناقش مشروعك شخصياً.'
  }
};

export const isExhibitionActive = () => new Date() < EXHIBITION_END;

export const EXHIBITION_OPEN_EVENT = 'gv:exhibition-modal:open';

export const openExhibitionModal = () => {
  window.dispatchEvent(new CustomEvent(EXHIBITION_OPEN_EVENT));
};

export const ExhibitionModal = () => {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const t = content[language] || content.ru;

  useEffect(() => {
    if (!isExhibitionActive()) return;
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    let timer;
    if (!dismissedAt || (Date.now() - parseInt(dismissedAt, 10)) >= DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000) {
      timer = setTimeout(() => setOpen(true), 1200);
    }
    const handleOpen = () => setOpen(true);
    window.addEventListener(EXHIBITION_OPEN_EVENT, handleOpen);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(EXHIBITION_OPEN_EVENT, handleOpen);
    };
  }, []);

  const handleClose = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = PROMO_CODE;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (_) {}
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn"
      data-testid="exhibition-modal"
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white/90 hover:bg-white text-[#061d36] shadow-md transition-all"
          data-testid="exhibition-modal-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          <div className="hidden md:block bg-[#061d36]">
            <img
              src="/assets/exhibition-kazbuild.png"
              alt="KazBuild 2026 приглашение"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>

          <div className="p-6 md:p-8 bg-gradient-to-br from-white via-white to-[#F5F5F0]">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#8b4444]/10 text-[#8b4444] text-xs font-bold tracking-wider uppercase mb-3">
              <span className="w-2 h-2 rounded-full bg-[#8b4444] animate-pulse" />
              KazBuild 2026
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-[#061d36] leading-tight mb-2">{t.heading}</h2>
            <p className="text-sm text-[#061d36]/60 mb-5">{t.subheading}</p>

            <div className="space-y-3 mb-5">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-[#8b4444] flex-shrink-0 mt-0.5" />
                <span className="text-[#061d36] font-semibold">{t.dates}</span>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#8b4444] flex-shrink-0 mt-0.5" />
                <span className="text-[#061d36] text-sm">{t.place}</span>
              </div>
              <div className="flex items-start gap-3">
                <Ticket className="w-5 h-5 text-[#8b4444] flex-shrink-0 mt-0.5" />
                <span className="text-[#061d36] font-bold">{t.booth}</span>
              </div>
            </div>

            <div className="bg-[#061d36] rounded-xl p-4 mb-5">
              <p className="text-xs text-white/60 uppercase tracking-wider mb-2">{t.promoLabel}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-lg md:text-xl font-mono font-bold text-white bg-white/10 px-3 py-2 rounded-lg tracking-wider" data-testid="exhibition-promo-code">
                  {PROMO_CODE}
                </code>
                <button
                  onClick={handleCopy}
                  className="p-2.5 rounded-lg bg-[#8b4444] hover:bg-[#7a3838] text-white transition-all"
                  data-testid="exhibition-copy-promo"
                  aria-label={t.copy}
                  title={t.copy}
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
              {copied && <p className="text-xs text-green-300 mt-2">{t.copied}</p>}
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <a
                href={REGISTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleClose}
                className="flex-1 text-center px-5 py-3 rounded-lg bg-[#8b4444] hover:bg-[#7a3838] text-white font-bold transition-all shadow-lg hover:shadow-xl"
                data-testid="exhibition-register-btn"
              >
                {t.register}
              </a>
              <button
                onClick={handleClose}
                className="px-5 py-3 rounded-lg border border-[#061d36]/20 text-[#061d36] font-medium hover:bg-[#061d36]/5 transition-all"
                data-testid="exhibition-later-btn"
              >
                {t.later}
              </button>
            </div>

            <p className="text-xs text-[#061d36]/50 mt-4 italic leading-snug">{t.footer}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExhibitionModal;
